import _ from "lodash";
import {atom} from "jotai";
import dayjs from "dayjs";
import {loadable} from "jotai/utils";
import {activeCompanyIdAtom} from "./activeCompanyStore";
import {routerFarmIdAtom, routerFieldIdAtom} from "./routerStore";
import {getCompanyResultsDates} from "../services/resultsService";

/**
 * Feature flag to show all calendar dates in the date picker, by companyId
 * When enabled, we want to avoid /results-dates call, so we want to use yesterday date instead of activeCompanyResultsDatesAtom
 */
export const showAllCalendarDatesFeatureFlagAtom = atom<boolean | undefined>();

/**
 * List of dates for Active Entity.
 * Active Entity is a Company, Farm or Field selected by User
 */
export const activeEntityDateOptionsAtom = atom<Promise<dayjs.Dayjs[]>>(async (get) => {
    const activeCompanyId = await get(activeCompanyIdAtom);
    const showAllCalendarDates = get(showAllCalendarDatesFeatureFlagAtom);
    if (showAllCalendarDates === undefined || showAllCalendarDates === true) return [];

    if (!activeCompanyId) return [];

    const dates = await getCompanyResultsDates(activeCompanyId, get(routerFarmIdAtom), get(routerFieldIdAtom));

    return _.uniq(dates).map(dayjs);
});

export const activeEntityDateOptionsLoadable = loadable(activeEntityDateOptionsAtom);

/**
 * A date explicitly selected by User. Overrides Default Date selection
 */
export const activeDateUserSelectionAtom = atom<dayjs.Dayjs | null>(null);

/**
 * A date explicitly selected by User in a Split Map view. Overrides Default Date selection
 */
export const splitViewDateUserSelectionAtom = atom<dayjs.Dayjs | null>(null);

/**
 * Default date selection (last date available from selected entity)
 */
export const activeDateDefaultSelectionAtom = atom<Promise<dayjs.Dayjs | null>>(async (get) => {
    const showAllCalendarDates = get(showAllCalendarDatesFeatureFlagAtom);
    if (showAllCalendarDates === undefined) return null;
    if (showAllCalendarDates === true) return dayjs().subtract(1, "day");

    const activeDateOptions = await get(activeEntityDateOptionsAtom);
    const activeDateUserSelection = get(activeDateUserSelectionAtom);

    if (activeDateUserSelection && activeDateOptions.includes(activeDateUserSelection)) {
        return activeDateUserSelection;
    } else {
        return _.max(activeDateOptions) ?? null;
    }
});

export const activeDateDefaultSelectionLoadable = loadable(activeDateDefaultSelectionAtom);

/**
 * Composed Atom for Active Date. Returns user's selection if available, otherwise - default selection.
 */
export const activeDateAtom = atom(async (get) => {
    return get(activeDateUserSelectionAtom) ?? (await get(activeDateDefaultSelectionAtom));
});

export const activeDateLoadable = loadable(activeDateAtom);

/**
 * Composed Atom for Active Date. Returns user's selection if available, otherwise - default selection.
 */
export const splitViewDateAtom = atom(async (get) => {
    return get(splitViewDateUserSelectionAtom) ?? (await get(activeDateDefaultSelectionAtom));
});

export const splitViewDateLoadable = loadable(splitViewDateAtom);
